import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
  useRevalidator,
  useSubmit,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useClickAway } from 'react-use';
import * as Sentry from '@sentry/react';

// Queries
import {
  GET_ACTIVE_SURVEILLANCE_ROUTES,
  GET_ACTIVE_ROUTE_SEGMENT,
} from '../../../gql/queries/surveillanceRoute';
import { GET_CONCESSIONS } from '../../../gql/queries/orders/orders';
import { FETCH_SURVEILLANCE_TEAMS } from '../../../gql/queries/users';

// Hooks
import { useAuth } from '../../../hooks/useAuth';
import { useWakeLock } from '../../../hooks/useWakeLock';
import { useAppUpdate } from '../../../hooks/useAppUpdate';
import { useBackgroundLocationGate } from '../../../hooks/useBackgroundLocationGate';
import { usePhotoPermissionsGate } from '../../../hooks/usePhotoPermissionsGate';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { PHOTO_GEOTAG_FLAG } from '../../modals/submodals/OpenCameraModal/useCaptureLocation';
import { useGpsCaptureGuardian } from '../../../hooks/useGpsCaptureGuardian';
import { useLastKnownOnError } from '../../../hooks/useLastKnownOnError';
import { useDirectionChangeDetection } from '../../../hooks/useDirectionChangeDetection';
import { useLocalGpsTrackFeed } from '../../../hooks/useLocalGpsTrackFeed';
import { useSurveillanceRoutes } from '../../../hooks/useSurveillanceRoutes';
import { usePendingSegments } from '../../../hooks/usePendingSegments';
import { useSegmentPhotos } from '../../../hooks/useSegmentPhotos';
import { useGpsGate } from '../../../hooks/useGpsGate';
import { hasSyntheticId } from '../../../offline/pendingSegmentsFromQueue';

// Utils
import {
  showDirectionChangeToast,
  dismissDirectionChangeToast,
} from '../../../utils/toasts';
import {
  setDirectionBlindReason,
  setDirectionOffRouteRoad,
} from '../../../state/directionDetection';
import { useSupportRoadCandidates } from '../../../hooks/useSupportRoadCandidates';
import { useSurveillanceRouteRoads } from '../../../hooks/useSurveillanceRouteRoads';
import { useAutoAddSupportRoad } from '../../../hooks/useAutoAddSupportRoad';
import { createFormDataFromValues } from '../../../utils/forms';
import {
  autoSelectableRouteId,
  buildRoidFallback,
  canEditSurveillanceRoute,
  canOperateTimer,
  hasOwnOpenSegment,
  directionNameKey,
  invertDirection,
  laneName,
  rememberFromSegment,
  isOwnSurveillanceRoute,
  routeDisplayName,
} from '../../../utils/surveillanceRoute';

// Redux
import { openModal, closeModal } from '../../../state/actions/ui/modal';
import { modalIds } from '../../modals/SuperModal/types';

// Components
import { ButtonIconBackground } from '../../inputs/buttons/ButtonIconBackground';
import { Typography } from '../../display/Typography';

// Icons
import { Clock } from '../../../assets/icons/ClockIcon';

// Hooks
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';

// Types
import { Role } from '../../../types/auth';
import {
  SurveillanceRoute,
  RouteSegment,
  RouteState,
} from '../../../types/surveillanceRoute';
import { StartTimerConfirmPayload } from '../../modals/submodals/NewSurveillanceSegmentModal/types';
import { PauseTimerConfirmPayload } from '../../modals/submodals/PauseSurveillanceRouteModal/types';
import { ChangeDirectionConfirmPayload } from '../../modals/submodals/ChangeDirectionModal/types';
import { showSuccessToast } from '../../../utils/toasts';
import { endRoadIdOf } from '../../../utils/roadStretches';
import {
  forgetRouteDelivery,
  rememberRouteDelivery,
} from '../../../offline/pendingRouteDelivery';
import { useResumeRouteDelivery } from '../../../hooks/useResumeRouteDelivery';
import {
  hasOwnOpenSegmentIn,
  shiftQuestionDecision,
  shouldAskAboutRoute,
} from '../../../utils/staleShift';
import { format } from 'date-fns';

// Styles — refactor v4 2026-05-27 (spec FR-10.1 "reuso del componente
// existente" + Toggl/Clockify-style pill solo en active):
//   - IDLE: ButtonIconBackground variant clock (REUSO del shared,
//     icono 2.4rem para que se vea pulsable; badge ActiveDot como
//     child si hay tramos activos en otros RVs no seleccionados).
//   - ACTIVE: TimerTriggerPill local (pill garnet con HH:MM:SS).
//   - Popover idéntico a `DropDownUserMenu`.
import {
  ContainerOverlay,
  TimerTriggerPill,
  PillIconWrap,
  ActiveDot,
  Popover,
  HorizontalLine,
  Section,
  InfoBlock,
  ButtonRow,
  ActionButton,
  HintText,
  SegmentInfoText,
  OverlayDropdown,
} from './styles';
import {
  useRoadNamesById,
  useRoadRangesById,
} from '../../../hooks/useRoadDirectionLabels';

const POLL_INTERVAL_MS = 30_000;

/**
 * Referencia estable: `usePendingSegments` memoiza por el array de tramos del
 * servidor, y un `[]` recién creado en cada render lo invalidaría siempre.
 * Es el caso «no hay tramo activo en el servidor»; cuando lo hay se le pasa
 * (ver `serverSegmentsForTimer`).
 */
const NO_SERVER_SEGMENTS: never[] = [];

// Recordatorio de tramo largo (rv-recorrido-colgado 2026-07-22): un tramo
// (segmento de conducción entre pausas) normal dura minutos; a las 4h ya es
// claramente raro (aviso suave), a las 8h es un olvido (alerta). Ataca la
// causa raíz de los recorridos colgados. Mirror del STALE_ROUTE_HOURS backend.
const SEGMENT_LONG_WARN_HOURS = 4;
const SEGMENT_LONG_ALERT_HOURS = 8;

/** Horas transcurridas desde `startDate` (0 si inválido). */
const elapsedHours = (startDate: string | Date): number => {
  const start = new Date(startDate).getTime();
  if (isNaN(start)) return 0;
  return Math.max(0, Date.now() - start) / 3_600_000;
};

const formatPkString = (km: number, meter: number): string =>
  `${km}+${meter.toString().padStart(3, '0')}`;

const formatElapsed = (startDate: string | Date): string => {
  const start = new Date(startDate).getTime();
  if (isNaN(start)) return '00:00:00';
  const elapsedMs = Math.max(0, Date.now() - start);
  const totalSec = Math.floor(elapsedMs / 1000);
  const hh = Math.floor(totalSec / 3600)
    .toString()
    .padStart(2, '0');
  const mm = Math.floor((totalSec % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const ss = (totalSec % 60).toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

/**
 * V4 P10 + D3 — TimerOverlay (2026-04-22).
 *
 * Popover montado en el TopBar con el control del timer del RV activo.
 * - Botón icono reloj con badge dot verde si hay tramo activo.
 * - Click → popover con: selector concesión (auto si 1), selector equipo
 *   vigilancia (isSurveillance=true, auto si 1), selector RV activo
 *   (in-progress|paused), cronómetro vivo si hay tramo, botones Tablet
 *   (Iniciar/Canvi/Finalitzar) o read-only (Coex/SuperAdmin/RoomTeam).
 * - Polling 30s (reval periódico, FR-10.5).
 */
export const TimerOverlay: React.FC = () => {
  const { t } = useTranslation(['modals', 'pages', 'common', 'errors']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const submit = useSubmit();
  // Fix 2026-07-21: las páginas RV (detalle + listado) leen SNAPSHOT del
  // loader (`useAsyncValue`), no la caché Apollo en vivo — el `refetchRoutes`
  // de abajo solo refresca las queries propias del overlay. Sin revalidar,
  // la tabla de tramos/mapa/cabecera del detalle quedaban congelados tras
  // iniciar tramo / cambiar sentido / pausar hasta un F5. Mismo patrón
  // canónico que `useRouteTimerActions` y `RouteSegmentOT`.
  const revalidator = useRevalidator();
  const { user } = useAuth();
  const currentUser = user?.user;
  const currentRole = currentUser?.rolActive as Role | undefined;
  const isTablet = currentRole === Role.Tablet;
  // Fix ronda 4 (D11): los botones de operar exigen Tablet DEL EQUIPO del RV
  // (mismo guard que RoutePreviewPage/RouteHeaderCard); antes bastaba el
  // rol y el backend rechazaba después con validateTimerOwnership.
  const isLandscapeTablet = useIsLandscapeTablet();
  // Tamaño de label de sección — espejo exacto del que usa UserMenu
  // (XS en landscapeTablet, S en desktop), con `variant="medium"`.
  const labelSize = isLandscapeTablet ? 'XS' : 'S';

  const [isOpen, setIsOpen] = useState(false);
  const [concessionId, setConcessionId] = useState<string | undefined>();
  const [teamId, setTeamId] = useState<string | undefined>();
  const [selectedRouteId, setSelectedRouteId] = useState<string | undefined>();
  // Prevención P1 (QA T6): tic de un minuto con el panel abierto, para que un
  // recorrido que se hace viejo con el panel abierto se pregunte igual.
  const [shiftClockTick, setShiftClockTick] = useState(0);
  // Re-render trigger para el cronómetro en vivo — el valor no se usa,
  // solo el setState fuerza re-render cada segundo cuando el popover está abierto.
  const [, setNowTick] = useState(Date.now());

  const popoverRef = useRef<HTMLDivElement>(null);
  /**
   * QA T6 (banco, 15-oct): tras «Seguir en este» el panel se abre en el MISMO
   * toque, y un doble toque dejaba caer el segundo fuera del panel —donde
   * estaba el modal que se cierra— y lo volvía a cerrar al instante. Durante
   * un momento tras abrirlo así, un toque fuera no lo cierra.
   */
  const ignoreClickAwayUntilRef = useRef(0);
  useClickAway(popoverRef, () => {
    if (Date.now() < ignoreClickAwayUntilRef.current) return;
    setIsOpen(false);
  });

  const { data: concessionsData } = useQuery(GET_CONCESSIONS, {
    fetchPolicy: 'cache-first',
  });
  const concessions = concessionsData?.concessions ?? [];

  const { data: teamsData } = useQuery(FETCH_SURVEILLANCE_TEAMS, {
    variables: { concessionId },
    fetchPolicy: 'cache-first',
  });
  const teams = teamsData?.surveillanceTeams ?? [];

  // Auto-select concession if tenant has only 1.
  useEffect(() => {
    if (!concessionId && concessions.length === 1) {
      setConcessionId(concessions[0].id);
    }
  }, [concessions, concessionId]);

  // Auto-select team if only 1 with isSurveillance=true.
  useEffect(() => {
    if (!teamId && teams.length === 1) {
      setTeamId(teams[0].id);
    }
  }, [teams, teamId]);

  const {
    data: activeRoutesData,
    error: activeRoutesError,
    refetch: refetchRoutes,
  } = useQuery(GET_ACTIVE_SURVEILLANCE_ROUTES, {
    // `isTablet` (revisión W5, M6): solo la tablet pide el fin del último tramo
    // de cada recorrido (P1); el resto de roles no paga esa lectura cada 30 s.
    variables: { input: { concessionId, teamId }, isTablet },
    fetchPolicy: 'cache-and-network',
    pollInterval: POLL_INTERVAL_MS,
  });
  // 🔴 Un sondeo fallido NO es «no hay recorridos». Con el `errorPolicy` por
  // defecto, un fallo de red deja `data` en `undefined`; leído en crudo, eso
  // vaciaba la selección, apagaba el detector de sentido y le quitaba el
  // recorrido al guardián durante un ciclo. Se conserva el último dato bueno
  // mientras dure el error, atado a las mismas variables.
  const activeRoutesSnapshot = useLastKnownOnError(
    activeRoutesData,
    activeRoutesError,
    `${concessionId ?? ''}|${teamId ?? ''}`
  );
  /** `true` solo cuando de verdad se sabe qué recorridos hay activos. */
  const routesKnown = activeRoutesSnapshot !== undefined;
  const activeRoutes: SurveillanceRoute[] =
    activeRoutesSnapshot?.activeSurveillanceRoutes ?? [];

  /**
   * Prevención P1 (plan 2026-10-15 § 5): recorridos de OTRO turno que este
   * operario ya dijo que sigue («Seguir en este»), para no volver a preguntar
   * en esta sesión.
   */
  const continuedStaleRouteIdsRef = useRef<Set<string>>(new Set());

  /**
   * QA T6b (banco, 15-oct): la última elección A MANO en el desplegable. Si
   * la pregunta sale por ella y se descarta (Escape, toque fuera), se vuelve a
   * lo que había antes en vez de quedarse con el recorrido viejo sin contestar
   * —que hacía que cada toque al cronómetro volviera a preguntar—.
   */
  const lastUserSelectionRef = useRef<{
    from?: string;
    to?: string;
    at: number;
  } | null>(null);

  /** La pregunta en curso: por qué recorrido y a qué volver si se descarta. */
  const pendingQuestionRef = useRef<{
    routeId: string;
    restoreTo?: string;
    answered: boolean;
    shown: boolean;
  } | null>(null);

  /**
   * 🔴 ¿Hay que preguntar antes de elegir este recorrido? Su.3827 y Su.3898: la
   * noche dejó su recorrido PAUSADO en «Fin de servicio» sin mandarlo a
   * validar, el turno siguiente abrió el cronómetro, el único activo era ese
   * —y se llama igual que el suyo— y el cronómetro lo eligió solo. Dos turnos
   * en un recorrido: horas infladas o borradas.
   *
   * Solo a la tablet (es quien graba tramos; los demás roles solo miran) y con
   * lo que hay en caché, así que sin red pregunta igual.
   */
  /**
   * Revisión W5 (M1): el recorrido que ESTA tablet está grabando (en curso y
   * suyo, la condición de `capturingRoute`) no se pregunta nunca. Explícito:
   * antes dependía del orden de los efectos, y con el panel abierto antes del
   * primer sondeo se preguntaba por el recorrido que estaba rodando.
   */
  const isCapturedByThisDevice = useCallback(
    (route: SurveillanceRoute) =>
      route.state === RouteState.IN_PROGRESS &&
      canOperateTimer(currentUser, route),
    [currentUser]
  );

  /**
   * QA T6 (banco, 15-oct): solo se pregunta por recorridos en los que esta
   * tablet podría iniciar tramo — la MISMA regla que enseña «Iniciar tramo»
   * (`canOperateTimer` y ningún otro cronómetro suyo en marcha). Por el de
   * otra patrulla la pregunta no llevaba a nada.
   */
  const canStartSegmentIn = useCallback(
    (route: SurveillanceRoute) =>
      canOperateTimer(currentUser, route) &&
      // QA T6b: un tramo abierto de esta cuenta en OTRO recorrido, como el back.
      !activeRoutes.some(
        (other) =>
          other.id !== route.id && hasOwnOpenSegmentIn(other, currentUser?.id)
      ),
    [currentUser, activeRoutes]
  );

  const needsShiftQuestion = useCallback(
    (route: SurveillanceRoute) =>
      isTablet &&
      shouldAskAboutRoute(route, {
        continuedRouteIds: continuedStaleRouteIdsRef.current,
        isCapturedByThisDevice,
        canStartSegmentIn,
        now: new Date(),
      }),
    [isTablet, isCapturedByThisDevice, canStartSegmentIn]
  );

  // Qué recorrido elige SOLO el cronómetro (`autoSelectableRouteId`, spec
  // 2026-10-22-cronometro-otra-patrulla): en una tablet, solo si la lista
  // tiene UN recorrido y es suyo —nunca el de otra patrulla ni uno libre—;
  // quien solo mira conserva «si solo hay uno, ese». Y, de esta pila (P1),
  // tampoco si es de otro turno: ese se pregunta al abrir el cronómetro
  // (`handleTogglePopover`). Resolución de la «Task 9» (docs/superpowers/specs/
  // 2026-10-22-propuestas-para-turnos-del-rv.md § 2).
  useEffect(() => {
    const autoRouteId = autoSelectableRouteId(currentUser, activeRoutes);
    const autoRoute = autoRouteId
      ? activeRoutes.find((r) => r.id === autoRouteId)
      : undefined;
    if (!selectedRouteId && autoRoute && !needsShiftQuestion(autoRoute)) {
      setSelectedRouteId(autoRoute.id);
    }
    // If the selected RV disappeared from the list (e.g. finalized elsewhere),
    // clear the selection — pero solo cuando la lista se CONOCE. Sin
    // `routesKnown`, un sondeo fallido se leía como «el recorrido ya no está»
    // y vaciaba la selección; con más de un recorrido activo, el auto-select de
    // arriba no la recupera y el detector de sentido queda apagado hasta que
    // alguien vuelve a elegir en el desplegable.
    if (
      routesKnown &&
      selectedRouteId &&
      !activeRoutes.some((r) => r.id === selectedRouteId)
    ) {
      setSelectedRouteId(undefined);
    }
  }, [
    activeRoutes,
    routesKnown,
    selectedRouteId,
    needsShiftQuestion,
    currentUser,
  ]);

  const selectedRoute = useMemo(
    () => activeRoutes.find((r) => r.id === selectedRouteId),
    [activeRoutes, selectedRouteId]
  );
  // Carreteras del recorrido elegido, de su entidad en caché (2026-09-23): las
  // que ven los modales de abrir, cerrar y cambiar de sentido. Ver
  // `useSurveillanceRouteRoads` para por qué no sirve el resultado del sondeo.
  const selectedRouteRoads = useSurveillanceRouteRoads(selectedRoute?.id);
  const selectedRoadIds =
    selectedRouteRoads?.roadIDs ?? selectedRoute?.roadIDs ?? undefined;
  const selectedConcessionIds =
    selectedRouteRoads?.concessionIDs ?? selectedRoute?.concessionIDs;

  const { data: activeSegmentData, error: activeSegmentError } = useQuery(
    GET_ACTIVE_ROUTE_SEGMENT,
    {
      variables: { surveillanceRouteId: selectedRouteId },
      skip: !selectedRouteId,
      fetchPolicy: 'cache-and-network',
      pollInterval: POLL_INTERVAL_MS,
    }
  );
  // 🔴 Mismo motivo que arriba, y aquí es donde dolió: el tramo activo pasaba a
  // `null` un ciclo por un sondeo fallido y el detector de cambio de sentido
  // reiniciaba su estado — el giro del km 194 del 2026-09-02 se perdió así. La
  // clave es el recorrido seleccionado: si cambia durante el corte, no se
  // hereda el tramo del anterior.
  const activeSegmentSnapshot = useLastKnownOnError(
    activeSegmentData,
    activeSegmentError,
    selectedRouteId
  );
  const serverActiveSegment: RouteSegment | null =
    activeSegmentSnapshot?.activeRouteSegment ?? null;

  // 🔴 Sin cobertura, el tramo que el operario acaba de iniciar SOLO existe en
  // la cola: la mutation está encolada y el servidor no puede devolver nada. La
  // pantalla se quedaba igual que antes de pulsar —sin cronómetro, sin fila— y
  // lo lógico entonces es volver a pulsar Iniciar, con lo que se encolaba un
  // segundo tramo que al sincronizar choca con el guard de "ya hay un timer
  // activo" y se descarta en silencio.
  //
  // Los pendientes se DERIVAN de la cola en vez de guardarse aparte: cuando el
  // replay borra la operación, el pendiente desaparece por construcción. No hay
  // un segundo estado que limpiar, que es donde estos mecanismos se pudren.
  //
  // Se le pasa el tramo activo del servidor —no una lista vacía— y no es un
  // detalle: `usePendingSegments` reconoce que un pendiente ya está confirmado
  // comparando contra los tramos del servidor, y suelta la retención cuando
  // CAMBIA su firma. Con `[]` fijo ninguna de las dos cosas puede pasar aquí, y
  // el tramo encolado seguía pintándose después de sincronizar.
  const serverSegmentsForTimer = useMemo(
    () => (serverActiveSegment ? [serverActiveSegment] : NO_SERVER_SEGMENTS),
    [serverActiveSegment]
  );
  const timerSegments = usePendingSegments(
    selectedRouteId,
    serverSegmentsForTimer
  );
  // 🔴 El tramo activo se lee de la lista FUSIONADA, nunca de
  // `serverActiveSegment` en crudo. Sin cobertura, el cierre de un tramo que el
  // servidor YA conocía se queda en la cola y su snapshot sigue diciendo
  // «abierto»: la tabla (que sí fusiona) lo enseñaba cerrado y con ⏳ mientras
  // este cronómetro seguía contándolo y ofrecía «Finalitzar» en vez de
  // «Iniciar» — C-25, 2026-09-08: 3,3 km / 2 min de patrulla sin tramo porque
  // no se podía abrir el siguiente hasta recuperar señal. El caso gemelo (tramo
  // NACIDO sin red y cerrado sin red) ya funcionaba porque ahí
  // `serverActiveSegment` era null y se caía a la lista. La precedencia «el
  // servidor gana» no cambia: la aplica la propia fusión (un cierre encolado
  // sobre un tramo que el servidor ya devuelve cerrado se ignora).
  const activeSegment: RouteSegment | null =
    timerSegments.find((segment) => !segment.endDate) ?? null;

  // Un tramo que solo existe en la cola no tiene id del servidor, pero SÍ se
  // puede cerrar: la operación se encola con el id sintético y el replay lo
  // traduce al real en cuanto el create se aplica (ver `remapPendingSegmentId`).
  // Prohibirlo dejaba al operario sin poder terminar el tramo ni empezar el
  // siguiente hasta recuperar señal, que en carretera pueden ser horas.
  // La marca se mantiene: el operario debe saber que aún no ha salido.
  const activeSegmentIsPending = hasSyntheticId(activeSegment);

  const hasActiveSegmentAnyRoute = activeRoutes.some(
    (r) => r.state === RouteState.IN_PROGRESS
  );

  // Regla de dominio "1 usuario = 1 timer activo" (feedback usuario
  // 2026-07-21): si el usuario YA tiene OTRO RV in-progress (= un tramo suyo
  // corriendo), ofrecer "Iniciar tramo" aquí es una trampa — el backend lo
  // rechaza con SR_TIMER_ACTIVE_ELSEWHERE. Se oculta el botón y se muestra el
  // motivo (misma traducción del código de negocio, cero keys nuevas).
  //
  // Fix 2026-07-30 (feedback usuario, dos tablets ÈXSER): la regla es "1
  // USUARIO = 1 timer", NO "1 equipo = 1 timer". ÈXSER admite VARIAS patrullas
  // activas a la vez (una por usuario), así que hay que filtrar por PROPIEDAD
  // (`isOwnSurveillanceRoute`) — igual que el `routeOptions` de abajo (🚗 vs
  // 🚙) y que el guard backend `assertNoOtherActiveTimer`, que es user-scoped.
  // Antes bastaba con que CUALQUIER RV estuviera in-progress → el operario B
  // veía el timer de A como suyo, se le ocultaba "Iniciar tramo" en su propio
  // RV y salía "Ya tienes un cronómetro activo" siendo falso. RVs sin `userId`
  // (legacy pre-ownership) no cuentan como propios → el backend es la
  // autoridad final (misma degradación que `canOperateTimer`).
  // Excluye el RV seleccionado: si el in-progress es ÉL mismo aplica la rama
  // activeSegment normal (Cambio/Finalizar).
  //
  // QA T6b (banco, 15-oct): «cronómetro activo» = un TRAMO ABIERTO de esta
  // cuenta, como lo decide el back (`assertNoOtherActiveTimer`), no el estado
  // `in-progress`. Un recorrido en curso sin tramos (un alta cuyo primer tramo
  // falló) bloqueaba a la tablet en todos los demás. Ver `hasOwnOpenSegmentIn`.
  const timerActiveElsewhere = activeRoutes.some(
    (r) => r.id !== selectedRouteId && hasOwnOpenSegmentIn(r, currentUser?.id)
  );

  /**
   * Wake Lock — vive AQUÍ, no en la página de detalle del RV.
   *
   * Antes lo pedía `RoutePreviewPage`, así que se liberaba en cuanto el
   * operario navegaba a otra vista (por ejemplo a crear una Actuación básica
   * desde el tramo, que es un flujo normal del recorrido) y la pantalla podía
   * dormirse a media faena. Es el mismo defecto que tenía el foreground
   * service de la app nativa: atar algo al ciclo de vida de un componente
   * cuando en realidad depende de una condición de negocio.
   *
   * El `TimerOverlay` se monta en el `Header`, presente en toda la app, y ya
   * conoce el tramo activo — así que la condición se cumple o no
   * independientemente de en qué pantalla esté el usuario.
   */

  /**
   * Permiso de ubicación en segundo plano. Solo se exige al rol **Tablet**: es
   * quien captura recorridos. Coex, RoomTeam o SuperAdmin pueden estar mirando
   * la app desde un despacho y bloquearles por un permiso de GPS que no van a
   * usar no tendría ningún sentido.
   *
   * El hook abre él mismo el modal `BACKGROUND_LOCATION_REQUIRED` cuando hace
   * falta; aquí no se pinta nada. En web es un no-op.
   */
  // (La llamada va más abajo, después de `capturingRoute`: con un recorrido
  // que esta tablet graba, su GPS pide lo suyo primero.)

  /**
   * Permisos para que las fotos guarden dónde se hicieron (spec 2026-09-23
   * fotos geolocalizadas, § 14 y § 15): la ubicación precisa (cámara de ROIS)
   * y la ubicación de las fotos (galería). A TODOS los roles de la APK, no solo
   * al Tablet: cualquiera puede hacer o subir fotos desde ella, y sin ellos la
   * foto sale sin GPS y sin avisar.
   *
   * Solo con el flag `feature-photo-geotag` del tenant (la ubicación de la
   * foto es dato del trabajador y primero se informa a la plantilla), y nunca
   * con un tramo en marcha: espera a que se cierre, igual que la actualización
   * obligatoria de justo arriba. En web es un no-op.
   */
  const photoGeotagEnabled = useFeatureFlag(PHOTO_GEOTAG_FLAG);

  /**
   * Guardián de la captura GPS. Vive aquí por la MISMA razón que el Wake Lock
   * de justo debajo: mientras haya un recorrido en marcha hay que vigilar que
   * se siga grabando, drenar lo capturado y mandarlo al servidor — y eso no
   * puede depender de que el operario tenga abierta una pantalla concreta.
   *
   * Antes vivía en la pestaña del mapa del recorrido, y salir de ella dejaba la
   * captura sin vigilancia: una tablet se pasó 47 horas sin grabar (12-ago-2026)
   * con la app en uso y el coche rodando, porque el sistema mató el servicio
   * mientras el operario estaba en otra pantalla y allí ya no había nadie
   * escuchando.
   *
   * 🔴 El recorrido a capturar se calcula de los recorridos ACTIVOS, nunca del
   * que esté seleccionado en el desplegable ni del que haya abierto en pantalla:
   * mirar un recorrido viejo no puede decidir si la patrulla graba o no.
   * `canOperateTimer` ya exige rol Tablet + equipo + propiedad, así que esto es
   * exactamente "el recorrido que ESTE dispositivo está haciendo".
   */
  const capturingRoute = useMemo(
    () =>
      activeRoutes.find(
        (r) =>
          r.state === RouteState.IN_PROGRESS && canOperateTimer(currentUser, r)
      ),
    [activeRoutes, currentUser]
  );

  /**
   * 🔴 El recorrido que lleva ESTA tablet se elige solo en el desplegable
   * (2026-09-29).
   *
   * El detector (giros y vía de apoyo) vigila el recorrido ELEGIDO, y el
   * desplegable solo se ponía solo con UN recorrido activo. En la UTE hay
   * varios del equipo a la vez, así que al iniciar desde la ficha —el
   * desplegable se quedaba con el del turno anterior— o al reabrir la app —sale
   * vacío— el detector estaba APAGADO hasta que el operario lo elegía a mano, y
   * nadie lo hace conduciendo. Cazado con el usuario: dos vueltas al ramal sin
   * añadir la vía, y la línea roja tampoco salía.
   *
   * Solo cuando CAMBIA el recorrido que se lleva (se inicia uno, o la app arranca
   * con uno en curso): si después el operario elige otro para mirarlo, se
   * respeta hasta el siguiente cambio.
   */
  const lastCapturingIdRef = useRef<string | null>(null);
  useEffect(() => {
    const capturingId = capturingRoute?.id ?? null;
    if (capturingId && capturingId !== lastCapturingIdRef.current) {
      setSelectedRouteId(capturingId);
    }
    lastCapturingIdRef.current = capturingId;
  }, [capturingRoute?.id]);

  /**
   * ¿Tiene ESTE aparato su propio tramo abierto? Decide si se le puede
   * interrumpir. Hasta el 2026-09-30 se usaba `!!activeSegment`, que es el tramo
   * del recorrido SELECCIONADO: un capataz que solo mira el recorrido del equipo
   * se quedaba sin avisos toda la jornada, y la tablet que patrulla, mirando
   * otro recorrido, perdía de vista el suyo. Ver `hasOwnOpenSegment`.
   */
  const ownOpenSegment = hasOwnOpenSegment({
    user: currentUser,
    selectedRoute,
    selectedRouteHasOpenSegment: !!activeSegment,
    capturingRouteId: capturingRoute?.id,
  });

  /**
   * Actualización de la app nativa. Vive aquí por el mismo motivo que el Wake
   * Lock: el `TimerOverlay` se monta en el Header, presente en toda la app, y
   * es quien sabe si hay un tramo corriendo.
   *
   * `ownOpenSegment` es la salvaguarda: mientras ESTA tablet tenga un tramo
   * abierto, la actualización ni se ofrece. En cuanto el operario pulsa
   * Finalizar aparece el aviso. No hace falta programar ninguna hora — el
   * momento seguro llega varias veces por turno.
   *
   * El hook no devuelve nada ni pinta nada: abre el modal compartido
   * (`VERSION_UPDATE_MODAL`, el mismo de la web) a través de Redux.
   *
   * En web hace lo mismo con la versión que sirve el dominio (`/version.json`),
   * solo en producción (spec 2026-09-30).
   */
  useAppUpdate({ hasActiveSegment: ownOpenSegment });

  // Va DESPUÉS de calcular `capturingRoute`: con un recorrido que esta tablet
  // graba, su GPS pide a Android la ubicación y las notificaciones al
  // arrancar, y el guardián de las fotos espera a que termine (medido en la W
  // el 25/09: sus diálogos salían encima del nuestro).
  usePhotoPermissionsGate({
    enabled: photoGeotagEnabled,
    hasActiveSegment: ownOpenSegment,
    routeCapturing: !!capturingRoute,
    // Al entrar la lista de recorridos tarda: sin esto el aviso se abría y
    // desaparecía en cuanto se sabía que esta tablet graba uno.
    routesKnown,
  });
  useBackgroundLocationGate(isTablet, {
    routeCapturing: !!capturingRoute,
    routesKnown,
  });

  useGpsCaptureGuardian({
    routeId: capturingRoute?.id,
    // Sin datos cargados no se sabe nada, y "no sé" nunca puede significar
    // "apaga la captura". Un sondeo fallido tampoco: `routesKnown` sale del
    // snapshot, que conserva el último dato bueno mientras dure el error.
    routesKnown,
  });

  /**
   * ═══ Detección de cambio de sentido ═══════════════════════════════════════
   *
   * Vive aquí por el MISMO motivo que el guardián de justo arriba: depende de
   * una condición de negocio —«hay un recorrido en curso que este dispositivo
   * está haciendo»— y no de qué pantalla tenga abierta el operario.
   *
   * Antes se montaba en la pestaña del mapa del recorrido, y eso lo rompía por
   * dos sitios a la vez:
   *
   *   1. Salir de esa pantalla —a crear la básica, a mirar otra OT, o a la
   *      pestaña de al lado— dejaba el giro SIN detectar. Ni aviso, ni
   *      registro automático, y sin que nadie se enterara.
   *   2. Con la pantalla apagada el `watch` no dispara (Android congela el
   *      WebView) y los puntos que sí graba el servicio nativo entraban por
   *      otra puerta que no alimentaba la traza de la pantalla. El detector se
   *      quedaba con su último punto visto y estampaba ahí el corte: el
   *      2026-09-01, en la C-25, DOS veces, en la gasolinera.
   *
   * La traza viene ahora del almacén local, por donde pasan los dos caminos.
   */
  const directionDetectionEnabled =
    !!capturingRoute &&
    // `activeSegment` se consulta para el recorrido SELECCIONADO: si no es el
    // que estamos capturando, no habla del tramo que hay que vigilar.
    selectedRouteId === capturingRoute.id &&
    !!activeSegment;

  const detectionTrack = useLocalGpsTrackFeed(
    capturingRoute?.id,
    directionDetectionEnabled
  );

  // Rangos declarados de las vías del recorrido: al registrar un giro en otra
  // vía, el tramo que cierra anota por cuáles pasó (como el cierre manual).
  // 🔴 Las carreteras, de la ENTIDAD en caché y no del resultado del sondeo
  // (2026-09-23): sin cobertura el sondeo falla y lo que queda es una foto que
  // no ve la vía de apoyo recién añadida. Ver `useSurveillanceRouteRoads`.
  const capturingRouteRoads = useSurveillanceRouteRoads(capturingRoute?.id);
  const capturingRoadIds =
    capturingRouteRoads?.roadIDs ?? capturingRoute?.roadIDs ?? undefined;
  const routeRoadRanges = useRoadRangesById(capturingRoadIds);
  // Vías de apoyo (2026-09-23): solo para NOMBRAR la vía en el aviso de
  // «fuera del trazado» y ofrecer añadirla; el detector no las sigue.
  const captureSupportRoadIds = useSupportRoadCandidates(
    capturingRoadIds,
    capturingRouteRoads?.concessionIDs ?? capturingRoute?.concessionIDs
  );

  const {
    pending: directionPending,
    blindReason: directionBlindReason,
    offRouteRoadId: directionOffRouteRoadId,
    supportRoadToAdopt,
    confirm: confirmDirectionChange,
    dismiss: rejectDirectionChange,
  } = useDirectionChangeDetection({
    routeId: capturingRoute?.id,
    // La vía con la que se abrió el tramo. El detector empieza vigilándola y
    // SIGUE a la patrulla si se pasa a otra del recorrido (2026-09-02: antes
    // se quedaba ciego en cuanto cruzaba, hasta cerrar el tramo).
    roadId: endRoadIdOf(activeSegment ?? {}) ?? capturingRoadIds?.[0],
    routeRoadIds: capturingRoadIds,
    routeRoadRanges,
    activeSegment,
    liveTrack: detectionTrack,
    enabled: directionDetectionEnabled,
    supportRoadIds: captureSupportRoadIds,
  });

  // Vía de apoyo (2026-09-29): si la patrulla SIGUE por una vía de su concesión
  // que el recorrido no declara, se añade sola y se avisa con un toast. Aquí y
  // no en la ficha: el aviso rojo de la ficha solo lo ve quien la tenga
  // abierta, y el operario conduciendo no mira una línea roja. Con permiso de
  // edición (la misma regla que el lápiz): `capturingRoute` ya es de esta
  // tablet, pero no se cuela nada que el lápiz no permitiría.
  const autoAddEnabled =
    !!capturingRoute &&
    canEditSurveillanceRoute(
      currentRole,
      capturingRoute.state,
      currentUser,
      capturingRoute
    );
  const { failedRoadId: supportRoadAddFailed } = useAutoAddSupportRoad({
    route: capturingRoute
      ? {
          id: capturingRoute.id,
          roadIDs: capturingRoadIds,
          roadRanges: capturingRoute.roadRanges,
        }
      : null,
    roadId: supportRoadToAdopt,
    enabled: autoAddEnabled,
    onAdded: () => revalidator.revalidate(),
  });
  // 🔴 En una vía de apoyo que se va a añadir sola, NO hay aviso rojo: la
  // frase «estás en la C-25z, que no es de este recorrido» + «Añadir» sobra
  // (el usuario, 2026-09-29) — se añade a las pocas lecturas y el toast lo
  // dice. Solo vuelve si el añadido automático FALLA (o no está permitido):
  // entonces es la salida a mano. Fuera de toda vía, el aviso de siempre.
  const supportRoadHandled =
    autoAddEnabled &&
    !!directionOffRouteRoadId &&
    directionOffRouteRoadId !== supportRoadAddFailed;
  const publishedBlindReason = supportRoadHandled ? null : directionBlindReason;
  const publishedOffRouteRoadId = supportRoadHandled
    ? null
    : directionOffRouteRoadId;

  // El aviso de «estoy ciego» se pinta bajo el mapa, que es un hermano de esta
  // cabecera y no un descendiente: se publica en vez de pasarse por contexto.
  useEffect(() => {
    setDirectionBlindReason(publishedBlindReason);
    return () => setDirectionBlindReason(null);
  }, [publishedBlindReason]);
  useEffect(() => {
    setDirectionOffRouteRoad(publishedOffRouteRoadId);
    return () => setDirectionOffRouteRoad(null);
  }, [publishedOffRouteRoadId]);

  // Latest-ref: con `toastId` fijo, react-toastify IGNORA un segundo `toast()`
  // mientras el primero sigue visible. Si los manejadores viajaran dentro del
  // elemento, se quedarían congelados en el primer pendiente y el «Sí»
  // registraría un pivote viejo.
  const confirmRef = useRef(confirmDirectionChange);
  confirmRef.current = confirmDirectionChange;
  const rejectRef = useRef(rejectDirectionChange);
  rejectRef.current = rejectDirectionChange;

  // Qué hay abierto en la ÚNICA ranura de modal de la app. El toast se pinta
  // por encima del modal a propósito (z-index 100001 > 100000), así que un
  // aviso que no se autocierra se quedaría TAPANDO el visor de cámara o el
  // modal de cerrar tramo hasta que alguien conteste.
  const openModalId = useSelector(
    (estado: { modal?: { id?: string | null } }) => estado.modal?.id ?? null
  );

  // Clave estable del pendiente: `setPending` se llama en cada lectura que
  // sigue siendo sospechosa, y el objeto cambia de identidad cada vez.
  const pendingPivotKey = directionPending?.pivot.timestamp ?? null;

  useEffect(() => {
    // 🔴 Retirar el aviso NO es responder «No». Aquí nunca se llama a
    // `rejectDirectionChange`: eso rechazaría el extremo y callaría el
    // detector 1 km sin que nadie lo hubiera decidido — y no se notaría,
    // porque parecería que «no ha detectado nada».
    if (pendingPivotKey === null || openModalId) {
      dismissDirectionChangeToast();
      return;
    }
    showDirectionChangeToast(
      () => {
        void confirmRef.current();
        dismissDirectionChangeToast();
      },
      () => {
        rejectRef.current();
        dismissDirectionChangeToast();
      }
    );
  }, [pendingPivotKey, openModalId]);

  // Al desmontar (logout, cierre de sesión) el aviso no puede quedar colgado.
  useEffect(() => () => dismissDirectionChangeToast(), []);

  useWakeLock(
    isTablet &&
      !!selectedRoute &&
      canOperateTimer(currentUser, selectedRoute) &&
      selectedRoute.state === RouteState.IN_PROGRESS &&
      !!activeSegment
  );

  // Live tick for chronometer — 1Hz mientras haya segmento activo.
  // Antes solo corría con popover abierto, pero ahora la pill del
  // TimerTrigger muestra el tiempo en el header SIN abrir el popover,
  // así que necesitamos tickear siempre que haya un segmento cargado.
  useEffect(() => {
    if (!activeSegment?.startDate) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [activeSegment?.startDate]);

  const { createSegment, finishSegment, finishRoute } = useSurveillanceRoutes();
  // P2 (QA T6b): completa una «Guardar y entregar» que una recarga dejó a
  // medias. Solo la tablet entrega recorridos desde aquí.
  useResumeRouteDelivery({
    enabled: isTablet,
    userId: currentUser?.id,
    finishRoute,
  });
  const guardSegmentStart = useGpsGate();
  const { openPhotoChain } = useSegmentPhotos();

  /**
   * Prevención P1: «¿Sigues el turno "<nombre>" del <dd/MM> o empiezas uno
   * nuevo?». Los dos gestos se resuelven AQUÍ, dentro del router: el modal vive
   * fuera y no puede navegar.
   */
  const askContinueShift = useCallback(
    (
      route: SurveillanceRoute,
      // Lo que el operario iba a hacer cuando se le preguntó (p. ej. «Iniciar
      // tramo»): con «Seguir en este» se hace, en vez de solo abrir el panel.
      options: { afterContinue?: () => void } = {}
    ) => {
      const shiftDay = route.creationDate ?? route.registerInitDate;
      // Si se descarta: la elección de antes si este recorrido se acaba de
      // elegir a mano; si no (llevaba elegido y se ha hecho viejo), ninguno —
      // el panel se puede abrir y elegir otro, y por este se vuelve a preguntar.
      const change = lastUserSelectionRef.current;
      const chosenJustNow =
        !!change && change.to === route.id && Date.now() - change.at < 10_000;
      pendingQuestionRef.current = {
        routeId: route.id,
        restoreTo: chosenJustNow ? change?.from : undefined,
        answered: false,
        shown: false,
      };
      const markAnswered = () => {
        if (pendingQuestionRef.current?.routeId === route.id) {
          pendingQuestionRef.current.answered = true;
        }
      };
      dispatch(
        openModal(modalIds.CONTINUE_SHIFT, {
          routeName: routeDisplayName(route),
          shiftDate: shiftDay ? format(new Date(shiftDay), 'dd/MM') : '',
          // «Seguir en este» = lo de siempre: queda elegido y se abre el panel.
          onContinue: () => {
            markAnswered();
            continuedStaleRouteIdsRef.current.add(route.id);
            setSelectedRouteId(route.id);
            dispatch(closeModal());
            if (options.afterContinue) options.afterContinue();
            else {
              ignoreClickAwayUntilRef.current = Date.now() + 700;
              setIsOpen(true);
            }
          },
          // «Empezar uno nuevo» = el asistente de alta. El viejo no se toca: se
          // deja de elegir (si lo estaba), y lo entrega o lo corrige quien
          // corresponda.
          onStartNew: () => {
            markAnswered();
            setSelectedRouteId((current) =>
              current === route.id ? undefined : current
            );
            dispatch(closeModal());
            navigate('/surveillance-route/new-route');
          },
        })
      );
    },
    [dispatch, navigate]
  );

  /**
   * ¿Hay que preguntar, y se puede AHORA? Por el elegido (revisión W5, I1:
   * tablet compartida entre turnos con la app abierta) o, sin nada elegido,
   * por el único activo; y sin pisar otro modal (M2: la ranura es única y
   * `openModal` reemplaza). Ver `shiftQuestionDecision`.
   */
  const pendingShiftQuestion = useCallback(
    () =>
      isTablet
        ? shiftQuestionDecision({
            routesKnown,
            activeRoutes,
            selectedRouteId,
            continuedRouteIds: continuedStaleRouteIdsRef.current,
            isCapturedByThisDevice,
            canStartSegmentIn,
            now: new Date(),
            modalSlotBusy: !!openModalId,
          })
        : null,
    [
      isTablet,
      routesKnown,
      activeRoutes,
      selectedRouteId,
      isCapturedByThisDevice,
      canStartSegmentIn,
      openModalId,
    ]
  );

  // P1 también con el panel YA abierto: los recorridos llegan después de
  // abrirlo (arranque de la app, antes del primer sondeo), el operario elige
  // en el desplegable uno de otro turno, o el elegido envejece con el panel
  // abierto. El panel se cierra y se pregunta. Con otro modal en la ranura se
  // espera: el efecto vuelve a correr cuando quede libre (`openModalId`). No
  // hay bucle: tras cerrar el modal sin contestar el panel queda cerrado.
  //
  // QA T6 (banco, 15-oct): «envejece con el panel abierto» necesita un RELOJ.
  // Sin él solo se reevaluaba al cambiar los recorridos o la ranura, y con la
  // tablet en el salpicadero y el panel abierto se pasaba la medianoche (o las
  // 6 h) sin preguntar. Un tic por minuto mientras el panel está abierto.
  useEffect(() => {
    if (!isOpen || !isTablet) return;
    const id = window.setInterval(
      () => setShiftClockTick((tick) => tick + 1),
      60_000
    );
    return () => window.clearInterval(id);
  }, [isOpen, isTablet]);

  useEffect(() => {
    if (!isOpen) return;
    const decision = pendingShiftQuestion();
    if (!decision?.askNow) return;
    setIsOpen(false);
    askContinueShift(decision.route);
  }, [isOpen, pendingShiftQuestion, askContinueShift, shiftClockTick]);

  // QA T6b: la pregunta se DESCARTA (Escape, toque fuera, la X) cuando su
  // modal se va sin respuesta. `BaseModal` cierra esos gestos por su cuenta,
  // sin pasar por ningún callback nuestro (memoria single_modal_slot_gotchas),
  // así que se mira la ranura en Redux. Descartar = volver a lo de antes.
  useEffect(() => {
    const question = pendingQuestionRef.current;
    if (!question) return;
    if (openModalId === modalIds.CONTINUE_SHIFT) {
      question.shown = true;
      return;
    }
    if (!question.shown) return;
    pendingQuestionRef.current = null;
    if (question.answered) return;
    setSelectedRouteId((current) =>
      current === question.routeId ? question.restoreTo : current
    );
  }, [openModalId]);

  const handleTogglePopover = useCallback(() => {
    // P1: al ABRIR el cronómetro con un recorrido de otro turno (el elegido, o
    // el único activo sin elegir), se pregunta en vez de enseñarlo. Cerrar el
    // modal sin contestar no elige nada: se vuelve a preguntar la próxima vez.
    // Con otro modal en la ranura (M2) tampoco se abre el panel: la pregunta
    // sigue pendiente para el siguiente intento.
    if (!isOpen) {
      const decision = pendingShiftQuestion();
      if (decision) {
        if (decision.askNow) askContinueShift(decision.route);
        return;
      }
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, pendingShiftQuestion, askContinueShift]);

  /**
   * Abre la ficha del recorrido que el panel ya está describiendo.
   *
   * El panel se cierra SIEMPRE — si se quedara abierto taparía justo la ficha
   * a la que acabas de llegar, que es el popover anclado al header.
   *
   * Y si ya estás en esa ficha no se navega: repetir la misma URL empuja una
   * entrada de historial y revalida los loaders, o sea un refetch del detalle
   * a cambio de nada. Sin cobertura eso además es un parpadeo gratis. Se
   * compara la ruta EXACTA a propósito: desde `/multimedia` del mismo
   * recorrido sí se sube al detalle.
   */
  const handleNavigateToRoute = useCallback(() => {
    if (!selectedRoute) return;
    setIsOpen(false);
    const target = `/surveillance-route/${selectedRoute.id}`;
    if (location.pathname === target) return;
    navigate(target);
  }, [navigate, location.pathname, selectedRoute]);

  const startSegmentNow = useCallback(() => {
    if (!selectedRoute) return;
    setIsOpen(false);
    const abrirVentanaDelTramo = () =>
      dispatch(
        openModal(modalIds.NEW_SURVEILLANCE_SEGMENT, {
          isLoading: false,
          // 🔴 Este es el ÚNICO camino para reanudar tras una pausa: el botón
          // del detalle solo sale con el RV `open`. Y aquí no hay tramos
          // cerrados a mano —solo se consulta el ACTIVO, que al reanudar es
          // `null`—, así que el PK del último cierre lo resuelve el modal con
          // este `routeId`. Sin él el campo salía vacío y había que esperar a
          // un GPS que además viene frío: con el RV en `paused` la captura está
          // apagada.
          routeId: selectedRoute.id,
          // Multi-carretera (2026-08-15): el modal busca el PK en TODAS y
          // resuelve en cuál está el operario.
          roadIds: selectedRoadIds,
          // Vías de apoyo (2026-09-23): las de su concesión, si la patrulla
          // está fuera de las del recorrido.
          concessionIds: selectedConcessionIds,
          onConfirm: async (payload: StartTimerConfirmPayload) => {
            try {
              await createSegment({
                surveillanceRouteId: selectedRoute.id,
                // Multi-carretera: la que eligió el operario en el modal.
                roadId: payload.roadId,
                direction: payload.direction,
                // Fix ronda 4: el modal envía top|down|both|sense especificar —
                // la comparación anterior con 'ascending' nunca acertaba y el
                // nombre salía siempre "descendente".
                directionName: t(
                  directionNameKey(payload.direction) ??
                    'pages:surveillance.directionUnspecified'
                ),
                // D1 (2026-07-23): el tramo nace SIN carril; se registra al cerrarlo.
                // 2026-08-10: y SIN descripción, por el mismo motivo.
                pkInitKm: payload.pkInitKm,
                pkInitMeter: payload.pkInitMeter,
              });
              refetchRoutes();
              // Refresca la página actual (loader snapshot) — detalle RV o listado.
              revalidator.revalidate();
            } catch (err) {
              Sentry.captureException(err, {
                tags: {
                  feature: 'surveillance-route',
                  component: 'TimerOverlay',
                  action: 'startSegment',
                },
              });
              // Sin toast aquí: useSurveillanceRoutes ya emite el de negocio
              // (backend-message-first, 2026-07-17) — no duplicar.
            }
          },
        })
      );

    // Misma puerta que en «Iniciar»: sin permiso de GPS no se abre tramo.
    void guardSegmentStart(abrirVentanaDelTramo);
  }, [
    selectedRoute,
    selectedRoadIds,
    selectedConcessionIds,
    guardSegmentStart,
    createSegment,
    dispatch,
    refetchRoutes,
    revalidator,
    t,
  ]);

  /**
   * 🔴 P1 en el último momento (QA T6, banco 15-oct): al pulsar «Iniciar
   * tramo», si el recorrido elegido se ha hecho de otro turno y nadie contestó
   * «Seguir en este» por él, se pregunta ANTES de iniciar. Es el gesto que P1
   * quiere proteger, y ningún reloj lo cubre del todo: el panel pudo quedarse
   * abierto horas. «Seguir en este» sigue con el inicio.
   */
  const handleStartTimer = useCallback(() => {
    if (!selectedRoute) return;
    if (needsShiftQuestion(selectedRoute)) {
      setIsOpen(false);
      askContinueShift(selectedRoute, { afterContinue: startSegmentNow });
      return;
    }
    startSegmentNow();
  }, [selectedRoute, needsShiftQuestion, askContinueShift, startSegmentNow]);

  const handleChangeDirection = useCallback(() => {
    if (!selectedRoute || !activeSegment) return;
    setIsOpen(false);
    const invertedDirection = invertDirection(activeSegment.direction);
    dispatch(
      openModal(modalIds.CHANGE_DIRECTION, {
        // UN solo PK = el punto de giro. NO caer al PK de inicio del tramo
        // activo: un tramo en curso nunca tiene pkEnd, así que antes el campo
        // traía el ARRANQUE del tramo y, si el operario confirmaba antes de que
        // el GPS resolviera, el tramo previo se cerraba en su propio inicio
        // (longitud cero, no se pintaba hasta el giro). Vacío hasta que GPS→PK
        // resuelva (o manual), igual que el modal de Finalizar (Task B3).
        defaultPk:
          activeSegment.pkEndKm != null && activeSegment.pkEndMeter != null
            ? { km: activeSegment.pkEndKm, meter: activeSegment.pkEndMeter }
            : undefined,
        // El PK se juzga contra la carretera del TRAMO, no contra la primera
        // del recorrido.
        roadId: activeSegment?.roadId ?? selectedRoadIds?.[0],
        // Lo que hace falta para saber por qué carreteras pasó el tramo que se
        // cierra, igual que el modal de Finalizar: todas las vías del
        // recorrido, el rastro grabado y dónde arranca el primer trozo.
        roadIds: selectedRoadIds,
        routeId: selectedRoute.id,
        segmentStartDate: activeSegment.startDate,
        segmentPkInit: {
          km: activeSegment.pkInitKm,
          meter: activeSegment.pkInitMeter,
        },
        defaultDirection: invertedDirection,
        defaultDescription: t('modals:changeDirection.description'),
        onConfirm: async (payload: ChangeDirectionConfirmPayload) => {
          try {
            // Patrón finish+create FR-12.3. Para el usuario es UNA operación:
            // silenciamos los toasts de éxito individuales ("Actualizado" +
            // "Creado") y emitimos uno solo al completar. Los errores siguen
            // sonando desde el hook (backend-message-first).
            const finished = await finishSegment(
              {
                id: activeSegment.id,
                pkEndKm: payload.pkEndKm,
                pkEndMeter: payload.pkEndMeter,
                pauseCause: 'basic',
                pauseCauseDetail: payload.pauseCauseDetail,
                // Por qué carreteras pasó. `undefined` = no tocar lo guardado.
                roadStretches: payload.roadStretches,
                // El carril NO viaja en un cambio de sentido: solo se sabe al
                // finalizar un tramo de verdad (decisión del usuario 2026-08-14,
                // la misma que sigue el camino automático).
              },
              { silentSuccess: true }
            );
            const created = await createSegment(
              {
                surveillanceRouteId: selectedRoute.id,
                // Un cambio de sentido NO cambia de carretera: se da la vuelta
                // en la misma. El tramo nuevo arranca en la vía donde el
                // anterior ACABÓ —que con trozos no es la de salida— y no en la
                // primera del recorrido, que es donde caía antes.
                //
                // 🔴 Los trozos que ACABAN de resolverse mandan sobre los del
                // tramo en memoria: hasta hoy este camino no los calculaba, así
                // que `endRoadIdOf` caía siempre a la vía de APERTURA y el tramo
                // nuevo nacía en la troncal aunque la patrulla hubiera girado
                // dentro del ramal (C-25, 2026-09-09).
                roadId:
                  endRoadIdOf({
                    roadId: activeSegment.roadId,
                    roadStretches: payload.roadStretches,
                  }) ?? undefined,
                direction: payload.newDirection,
                // D1: el tramo nuevo nace SIN carril; lo tendrá al cerrarse.
                // 2026-08-10: tampoco lleva descripción. El texto que escribe el
                // operario en este modal es del tramo que se CIERRA (viaja arriba
                // como `pauseCauseDetail`). Mandarlo también aquí reintroducía la
                // descripción-al-arrancar que este cambio retira.
                pkInitKm: payload.pkInitKm,
                pkInitMeter: payload.pkInitMeter,
              },
              { silentSuccess: true }
            );
            showSuccessToast(
              finished === null || created === null
                ? t('modals:changeState.queuedOffline')
                : t('toasts:success.updated')
            );
            refetchRoutes();
            // Refresca la página actual (loader snapshot) — detalle RV o listado.
            revalidator.revalidate();
          } catch (err) {
            Sentry.captureException(err, {
              tags: {
                feature: 'surveillance-route',
                component: 'TimerOverlay',
                action: 'changeDirection',
              },
            });
            // Sin toast aquí: useSurveillanceRoutes ya emite el de negocio
            // (backend-message-first, 2026-07-17) — no duplicar.
          }
        },
      })
    );
  }, [
    selectedRoute,
    selectedRoadIds,
    activeSegment,
    finishSegment,
    createSegment,
    dispatch,
    refetchRoutes,
    revalidator,
    t,
  ]);

  const handleFinishSegment = useCallback(() => {
    if (!selectedRoute || !activeSegment) return;
    setIsOpen(false);
    dispatch(
      openModal(modalIds.PAUSE_SURVEILLANCE_ROUTE, {
        // Fix 2026-07-23 (plan `rv-mapa-gps-tiempo-real`, Task B3 — queja
        // usuario "me ofrece el pk que empezamos"): ANTES se pasaba el PK de
        // INICIO del tramo (`activeSegment.pkInitKm/pkInitMeter`) como
        // sugerencia de cierre — engañoso, ese es de dónde vino la patrulla,
        // no dónde está ahora. `PauseTimerModal` YA resuelve GPS→PK on-mount
        // (`useGpsToPK`, con el umbral ampliado a 300m de B1 + el fallback
        // offline de B2) y sobrescribe el campo en cuanto tiene un fix — no
        // pasar `defaultPkEnd` deja el campo vacío hasta que el GPS resuelva
        // (o el operario lo escribe a mano si el GPS falla), en vez de
        // sugerir un PK estático y equivocado.
        // El PK se juzga contra la carretera del TRAMO, no contra la primera
        // del recorrido.
        roadId: activeSegment?.roadId ?? selectedRoadIds?.[0],
        // Para saber si hace falta decir en CUÁL: con una sola, no.
        roadIds: selectedRoadIds,
        // Vías de apoyo (2026-09-23): el tramo puede acabar en una de su
        // concesión que el recorrido no declara.
        concessionIds: selectedConcessionIds,
        // Para buscar en el rastro grabado dónde cruzó de carretera, si cruzó.
        routeId: selectedRoute.id,
        segmentStartDate: activeSegment.startDate,
        // Su PK inicial: es donde arranca el primer trozo.
        segmentPkInit: {
          km: activeSegment.pkInitKm,
          meter: activeSegment.pkInitMeter,
        },
        // D1 (2026-07-23): pre-rellenar el carril con el del tramo que se cierra.
        defaultLane: activeSegment.lane ?? '',
        // Prevención P2 (plan 2026-10-15 § 5): casilla «Último tramo del turno».
        offerLastSegment: true,
        onConfirm: async (payload: PauseTimerConfirmPayload) => {
          try {
            // P2: con la casilla marcada, cerrar el tramo y entregar son UNA
            // operación para el operario — un solo aviso al final, como la
            // cadena de «Finalizar» con tramo abierto (`useRouteTimerActions`).
            const isLastSegment = !!payload.isLastSegment;
            // El tramo se cierra ENTERO, por su causa, y registra por qué
            // carreteras pasó. No se parte: terminar un tramo porque la vía
            // cambió de nombre fabricaría un final sin causa.
            const finishedSegmentId = activeSegment.id;
            // 🔴 QA T6b (banco, 15-oct): la intención de ENTREGAR se guarda
            // ANTES de cerrar. Con red, la entrega espera la respuesta del
            // cierre en memoria, y una recarga (F5, la app que muere) en ese
            // segundo dejaba el tramo cerrado y el recorrido `paused` sin aviso.
            // Si eso pasa, la siguiente carga la completa
            // (`useResumeRouteDelivery`). Misma hora en las dos.
            const deliveryEndDate = new Date().toISOString();
            if (isLastSegment) {
              rememberRouteDelivery({
                routeId: selectedRoute.id,
                segmentId: finishedSegmentId,
                userId: currentUser?.id,
                registerEndDate: deliveryEndDate,
              });
            }
            const finishedSegment = await finishSegment({
              id: finishedSegmentId,
              pkEndKm: payload.pkEndKm,
              pkEndMeter: payload.pkEndMeter,
              pauseCause: payload.pauseCause,
              pauseCauseDetail: payload.pauseCauseDetail,
              // D1 (2026-07-23): carril del tramo que se cierra.
              lane: payload.lane,
              roadStretches: payload.roadStretches,
              // Dónde paró la tablet (spec aviso por zona § 6): el
              // autovínculo compara el aviso con la zona del trabajo.
              endLatitude: payload.endLatitude,
              endLongitude: payload.endLongitude,
            }, { routeId: selectedRoute.id, silentSuccess: isLastSegment });
            if (isLastSegment) {
              // P2: el MISMO flujo que el botón «Finalizar» del recorrido sin
              // tramo abierto (sin confirmación aparte: la casilla lo es). Sin
              // red se encola detrás del cierre del tramo y el replay FIFO los
              // aplica en orden; `finishSurveillanceRoute` ya viajaba en la
              // cola, así que no hay ningún rechazo nuevo.
              //
              // 🔴 Revisión W5 (M3): con su PROPIO try. Si entregar falla con
              // red, el tramo SÍ se cerró: se dice (el error de la entrega ya
              // lo enseña `finishRoute`) y se sigue con el refresco y las
              // fotos. Antes el fallo saltaba al catch común y parecía que no
              // se había guardado nada.
              let deliveredRoute: unknown = null;
              let deliveryFailed = false;
              try {
                deliveredRoute = await finishRoute(
                  { id: selectedRoute.id, registerEndDate: deliveryEndDate },
                  { silentSuccess: true }
                );
              } catch (deliveryError) {
                deliveryFailed = true;
                Sentry.captureException(deliveryError, {
                  tags: {
                    feature: 'surveillance-route',
                    component: 'TimerOverlay',
                    action: 'finishRouteAfterLastSegment',
                  },
                });
              }
              // Entregada, encolada o rechazada (con su aviso): la intención
              // ya no hace falta.
              forgetRouteDelivery(selectedRoute.id);
              showSuccessToast(
                finishedSegment === null ||
                  (!deliveryFailed && deliveredRoute === null)
                  ? t('modals:changeState.queuedOffline')
                  : t('toasts:success.updated')
              );
            }
            if (payload.roadStretches?.length)
              showSuccessToast(t('pages:surveillance.roadChangeSplit'));
            refetchRoutes();
            // Refresca la página actual (loader snapshot) — detalle RV o
            // listado. En la cadena causa=action el submit de abajo navega
            // al wizard y la revalidación en curso es inocua.
            revalidator.revalidate();
            // Intervenció lleu: se le ofrece al operario adjuntar las fotos de
            // lo que ha colocado o arreglado, que es lo que le piden en campo y
            // hasta ahora no tenía dónde meter. Son OPCIONALES: si cierra el
            // modal sin ninguna no pasa nada, y podrá añadirlas después desde
            // la card del tramo.
            //
            // NO va dentro del modal de cierre: el sistema de modales es de uno
            // en uno (`openModal` REEMPLAZA), así que abrir la cámara desde
            // allí destruiría el formulario con el PK y la causa dentro.
            //
            // Se ofrece también cuando el cierre se ENCOLÓ (sin red): el tramo
            // existe desde que se inició, su Order ya está en caché, y las
            // fotos se encolan igual — las sube el mismo drenado.
            if (payload.pauseCause === 'basic') {
              const segmentOrderId = activeSegment.orderIDs?.[0];
              if (segmentOrderId) {
                const photoTarget = {
                  segmentId: finishedSegmentId,
                  orderId: segmentOrderId,
                  indexExpedition: activeSegment.indexExpedition ?? 0,
                  indexType: activeSegment.indexType ?? 0,
                };
                // 🔴 El `setTimeout(0)` NO es cosmético. `PauseTimerModal`
                // llama a su `handleClose()` DESPUÉS de que este `onConfirm`
                // resuelva, y como el sistema de modales es de UNA sola ranura,
                // ese cierre se lleva por delante cualquier modal que hayamos
                // abierto aquí — la cadena de fotos se abría y desaparecía en el
                // mismo instante. Una microtarea tampoco vale: se ejecutaría
                // ANTES de esa continuación. Hace falta una macrotarea.
                setTimeout(() => openPhotoChain(photoTarget), 0);
              }
            }

            // FR-13.3/FR-9: si causa = action, entrar en P9 (Crear Actuació).
            // NewActionPage exige actionData del router (un GET con navigate()
            // rebota en su useLayoutEffect) → POST 'create-basic-action' con la
            // Order padre del RV, mismo contrato que el botón de ShowOT.
            //
            // SIN RED (2026-09-02): también se abre el wizard. Antes se gateaba
            // el cierre encolado para no abrirlo, porque el router action
            // degradaba al formulario de la normal; ahora siembra la básica sin
            // crear nada y la cola compound la crea al sincronizar. `fromSegment`
            // es el id del tramo: real si se inició con red, `pending-*` si no —
            // la cola lo traduce al reproducir el CreateRouteSegment antes de
            // que la compound vincule.
            if (payload.pauseCause === 'action') {
              const formData = createFormDataFromValues({
                id: selectedRoute.orderIDs?.[0] ?? '',
                roid: buildRoidFallback(selectedRoute),
                // P9 auto-create (2026-07-21): crea la básica heredando PKs/
                // sentido/carril del tramo + operarios del RV y aterriza en
                // el wizard reducido. La descripción de la causa viaja como
                // descripción de la actuación.
                intent: 'create-basic-action',
                ...(payload.pauseCauseDetail && {
                  description: payload.pauseCauseDetail,
                }),
              });
              // I2 (QA IA 2026-07-13): persistir el contexto del chain —
              // un F5 a mitad de wizard pierde el ?fromSegment de la URL.
              rememberFromSegment(selectedRoute.id, finishedSegmentId);
              submit(formData, {
                method: 'POST',
                action: `/surveillance-route/${selectedRoute.id}/new-action?fromSegment=${finishedSegmentId}`,
              });
            }
          } catch (err) {
            // El cierre falló (con su aviso): no hay nada que entregar.
            if (payload.isLastSegment) forgetRouteDelivery(selectedRoute.id);
            Sentry.captureException(err, {
              tags: {
                feature: 'surveillance-route',
                component: 'TimerOverlay',
                action: 'finishSegment',
              },
            });
            // Sin toast aquí: useSurveillanceRoutes ya emite el de negocio
            // (backend-message-first, 2026-07-17) — no duplicar.
          }
        },
      })
    );
  }, [
    selectedRoute,
    selectedRoadIds,
    selectedConcessionIds,
    activeSegment,
    finishSegment,
    finishRoute,
    dispatch,
    refetchRoutes,
    revalidator,
    navigate,
    openPhotoChain,
    t,
  ]);

  // Nombres de las carreteras de TODOS los recorridos del selector: la etiqueta
  // dice por qué vías va el recorrido, no un rango de PK.
  const idsDeCarreteras = useMemo(
    () => [...new Set(activeRoutes.flatMap((r) => r.roadIDs ?? []))],
    [activeRoutes]
  );
  const nombresDeCarretera = useRoadNamesById(idsDeCarreteras);

  const routeOptions = useMemo(
    () =>
      activeRoutes.map((r) => {
        // Label del selector: `Nom | PK inici → PK fi · <dueño>`. El PK ayuda a
        // ver el tramo que cubre el recorrido (decisión usuario 2026-07-30, se
        // mantiene). Para que el sufijo de dueño quepa junto al PK, "otra
        // patrulla" se acortó a "patrulla" (i18n `timerOtherPatrol`).
        //
        // El nombre a mostrar sale del helper compartido (2026-09-01): el
        // enlace a la ficha de aquí abajo usa el MISMO, así que el desplegable
        // y el enlace no pueden decir cosas distintas del mismo recorrido. La
        // cadena de respaldo —nombre → ROID canónico → etiqueta i18n— no ha
        // cambiado; solo ha dejado de estar copiada.
        const displayName = routeDisplayName(r);
        // 🔴 Las CARRETERAS, no un rango de PK (decisión usuario 2026-08-27).
        //
        // El rango se puso el 2026-07-30 para ver qué tramo cubre el recorrido,
        // cuando un recorrido era de UNA carretera. Con varias enseñaba el de la
        // PRIMERA y callaba las demás: `Test-BLA | 82+800 → 236+000` ocultaba
        // que también va por la C-25z. Y poner los dos rangos no cabe en la
        // línea. Los nombres orientan mejor y no mienten.
        //
        // Si los nombres aún no han llegado de datos maestros se cae al rango de
        // siempre: sin cobertura y con la caché fría, algo es mejor que nada.
        const nombresVias = (r.roadIDs ?? [])
          .map((id) => nombresDeCarretera[id])
          .filter(Boolean)
          .join(' + ');
        const pkRange =
          nombresVias ||
          `${formatPkString(r.pkInitKm, r.pkInitMeter)} → ${formatPkString(r.pkEndKm, r.pkEndMeter)}`;
        // Marcador de patrulla en curso. La premisa original — "1 usuario = 1
        // timer, así que el in-progress de tu equipo es el tuyo" — se cayó el
        // 2026-07-28: en ÈXSER puede haber VARIOS recorridos activos a la vez,
        // y con dos patrullas el coche a secas ya no distinguía nada.
        //
        // Ahora el color dice de quién es:
        //   🚗 rojo  → tuyo: es donde te salen Cambio de sentido y Finalizar
        //   🚙 azul  → de otra patrulla: lo ves, pero no lo operas
        //
        // Elegidos comprobando cómo los pinta de verdad una Galaxy Tab Active5,
        // no por su nombre Unicode: los dos difieren en color Y en silueta
        // (sedán vs todoterreno), así que siguen distinguiéndose si una
        // actualización cambia la paleta. No existe coche verde.
        const isOwn = isOwnSurveillanceRoute(currentUser, r);
        const isRunning = r.state === RouteState.IN_PROGRESS;
        const marker = isRunning ? (isOwn ? '🚗 ' : '🚙 ') : '';
        // Visibilidad de propiedad (2026-07-30): cada opción dice de quién es —
        // `tú` / `otra patrulla` (tiene dueño y no soy yo) / `libre` (sin dueño,
        // reabierto por gestor o sin iniciar). El nombre concreto de la otra
        // tablet se ve en la card del detalle; aquí basta el relacional para
        // elegir bien.
        const ownerSuffix = isOwn
          ? t('pages:surveillance.timerYou')
          : r.timerOwnerId
            ? t('pages:surveillance.timerOtherPatrol')
            : t('pages:surveillance.timerFree');
        return {
          value: r.id,
          label: `${marker}${displayName} | ${pkRange} · ${ownerSuffix}`,
        };
      }),
    [activeRoutes, t, currentUser, nombresDeCarretera]
  );

  const concessionOptions = useMemo(
    () =>
      concessions.map((c: { id: string; name: string }) => ({
        value: c.id,
        label: c.name,
      })),
    [concessions]
  );

  const teamOptions = useMemo(
    () =>
      teams.map((tm: { id: string; name: string }) => ({
        value: tm.id,
        label: tm.name,
      })),
    [teams]
  );

  const showConcessionSelector = concessions.length > 1;
  const showTeamSelector = teams.length > 1;

  // Modo del trigger:
  //   - ACTIVE pill: hay un activeSegment cargado (tramo en curso del
  //     RV seleccionado). Pill garnet horizontal con `[⏱] HH:MM:SS`.
  //   - IDLE: ButtonIconBackground variant clock (round garnet del
  //     set de tablas, reuso shared). Si hay tramos activos en otros
  //     RVs no seleccionados, badge dot verde como child.
  // D5 (2026-10-22): en una TABLET la barra solo enseña tiempo si el tramo es
  // de un recorrido que ella opera. Elegir a mano el de otra patrulla deja
  // verlo en el panel, pero la barra se lee como «mi cronómetro» y enseñarlo
  // ahí es lo que hacía creer al operario que seguía corriendo el suyo.
  // Quien solo mira (Coex, Sala…) la sigue viendo como siempre.
  const showActiveTimer =
    !!activeSegment?.startDate &&
    (!isTablet || canOperateTimer(currentUser, selectedRoute));
  const showIdleBadge = !showActiveTimer && hasActiveSegmentAnyRoute;
  // Tooltip se oculta cuando el popover está abierto (evita tooltip y
  // popover visibles a la vez sobre el mismo trigger).
  const tooltipText = isOpen ? undefined : t('modals:timerOverlay.title');

  // La actualización obligatoria ya NO se pinta aquí: es un modal de
  // `SuperModal` como el del permiso de segundo plano (PR #178), y lo levanta
  // `useAppUpdate`. Bloquea igual —no se cierra con la X, ni con Escape, ni
  // clicando fuera— pero deja de haber dos formas distintas de decir lo mismo.

  return (
    <ContainerOverlay ref={popoverRef}>
      {showActiveTimer && activeSegment ? (
        <TimerTriggerPill
          type="button"
          onClick={handleTogglePopover}
          data-tooltip={tooltipText}
          aria-label={t('modals:timerOverlay.title')}
        >
          <PillIconWrap>
            <Clock />
          </PillIconWrap>
          <Typography size="S" colorText="white" variant="semiBold">
            {formatElapsed(activeSegment.startDate)}
          </Typography>
        </TimerTriggerPill>
      ) : (
        <ButtonIconBackground
          variant="clock"
          type="button"
          onClick={handleTogglePopover}
          title={tooltipText}
          aria-label={t('modals:timerOverlay.title')}
        >
          {showIdleBadge && <ActiveDot aria-hidden="true" />}
        </ButtonIconBackground>
      )}

      {isOpen && (
        <Popover role="dialog" aria-label={t('modals:timerOverlay.title')}>
          {/* Cabecera del popover — equivalente al ContainerHeader de UserMenu */}
          <Typography
            size={isLandscapeTablet ? 'S' : 'M'}
            variant="semiBold"
            colorText="black"
          >
            {t('modals:timerOverlay.title')}
          </Typography>
          <HorizontalLine />

          {showConcessionSelector && (
            <>
              <Section>
                <Typography
                  size={labelSize}
                  colorText="greyDark"
                  variant="medium"
                >
                  {t('modals:timerOverlay.concession')}
                </Typography>
                <OverlayDropdown
                  inputSize="M"
                  typeDropDown="Default"
                  placeholder={t('common:labels.select')}
                  options={concessionOptions}
                  selectedOption={concessionId ?? ''}
                  onChangeSelected={(option) => setConcessionId(option?.value)}
                />
              </Section>
              <HorizontalLine />
            </>
          )}

          {showTeamSelector && (
            <>
              <Section>
                <Typography
                  size={labelSize}
                  colorText="greyDark"
                  variant="medium"
                >
                  {t('modals:timerOverlay.team')}
                </Typography>
                <OverlayDropdown
                  inputSize="M"
                  typeDropDown="Default"
                  placeholder={t('common:labels.select')}
                  options={teamOptions}
                  selectedOption={teamId ?? ''}
                  onChangeSelected={(option) => setTeamId(option?.value)}
                />
              </Section>
              <HorizontalLine />
            </>
          )}

          <Section>
            <Typography size={labelSize} colorText="greyDark" variant="medium">
              {t('modals:timerOverlay.route')}
            </Typography>
            {activeRoutes.length === 0 ? (
              <Typography size="XS" colorText="greyDark">
                {t('modals:timerOverlay.noRoutes')}
              </Typography>
            ) : (
              <OverlayDropdown
                inputSize="M"
                typeDropDown="Default"
                placeholder={t('common:labels.select')}
                options={routeOptions}
                selectedOption={selectedRouteId ?? ''}
                onChangeSelected={(option) => {
                  // QA T6b: para volver aquí si la pregunta se descarta.
                  lastUserSelectionRef.current = {
                    from: selectedRouteId,
                    to: option?.value,
                    at: Date.now(),
                  };
                  setSelectedRouteId(option?.value);
                }}
              />
            )}
          </Section>

          {selectedRoute && (
            <>
              <HorizontalLine />
              {activeSegment ? (
                <InfoBlock>
                  <Typography
                    size={isLandscapeTablet ? 'M' : 'L'}
                    variant="semiBold"
                    colorText="garnet"
                  >
                    {formatElapsed(activeSegment.startDate)}
                  </Typography>
                  <SegmentInfoText size="XS" colorText="greyDark">
                    {formatPkString(
                      activeSegment.pkInitKm,
                      activeSegment.pkInitMeter
                    )}
                    {activeSegment.direction
                      ? ` · ${t(
                          directionNameKey(activeSegment.direction) ??
                            'pages:surveillance.directionUnspecified'
                        )}`
                      : ''}
                    {activeSegment.lane
                      ? ` · ${laneName(activeSegment.lane)}`
                      : ''}
                  </SegmentInfoText>
                  {/* Recordatorio de tramo largo (rv-recorrido-colgado):
                      suave a 4h, alerta a 8h — avisa de que el tramo lleva
                      abierto más de lo normal (probable olvido de cierre); si
                      se deja colgado, las horas del recorrido salen infladas. */}
                  {(() => {
                    // Solo a quien puede cerrarlo desde aquí (2026-10-22): el
                    // texto le pide al operario que cierre SU tramo. A una
                    // tablet que mira el de otra patrulla —o a gestión— le
                    // pedía algo que no puede hacer. `canOperateTimer` ya es
                    // `false` para todo rol que no sea Tablet.
                    if (!canOperateTimer(currentUser, selectedRoute)) return null;
                    const hrs = elapsedHours(activeSegment.startDate);
                    if (hrs < SEGMENT_LONG_WARN_HOURS) return null;
                    const alert = hrs >= SEGMENT_LONG_ALERT_HOURS;
                    // Colores de aviso de verdad (prueba del usuario, 22-oct):
                    // `garnet` es el LILA principal del tema ROIS, no un rojo, y
                    // `gold` es un amarillo claro que no se lee sobre blanco. El
                    // rojo es el de error de la app (`red` = `error` del tema),
                    // el mismo de los toasts y del aviso del detector.
                    return (
                      <HintText
                        size="XS"
                        colorText={alert ? 'red' : 'secondaryOrange'}
                        data-testid="segment-long-warning"
                      >
                        {t(
                          alert
                            ? 'modals:timerOverlay.longSegmentAlert'
                            : 'modals:timerOverlay.longSegmentWarn',
                          { hours: Math.floor(hrs) }
                        )}
                      </HintText>
                    );
                  })()}
                  {/* El tramo existe solo en la cola. El cronómetro corre y se
                      puede cerrar igual —el replay traduce el id—, pero el
                      operario tiene que saber que todavía no ha salido de la
                      tablet. */}
                  {activeSegmentIsPending && (
                    <HintText
                      size="XS"
                      // Naranja de aviso, como el tramo largo de 4-8 h: `gold`
                      // no se lee sobre el panel blanco (usuario, 22-oct).
                      colorText="secondaryOrange"
                      data-testid="segment-pending-sync"
                    >
                      {t('modals:timerOverlay.pendingSyncSegment')}
                    </HintText>
                  )}
                </InfoBlock>
              ) : (
                <HintText size="XS" colorText="greyDark">
                  {timerActiveElsewhere
                    ? t('errors:backend.SR_TIMER_ACTIVE_ELSEWHERE')
                    : t('modals:timerOverlay.noActiveSegment')}
                </HintText>
              )}

              {/* Ir a la ficha del recorrido (2026-09-01).
                  Es el MISMO botón con el que se llega hoy al detalle desde el
                  listado de Recorridos (`SurveillanceRoutesPage`), con su misma
                  clave: `MainButton` + `pages:surveillance.seeRoute`. Cero
                  claves nuevas y cero estilos nuevos.
                  Va como acción y no como texto pulsable porque es lo que hace
                  este panel —y el del usuario, del que copia su anatomía—: toda
                  acción es un botón de ancho completo. El nombre del recorrido
                  no se repite aquí: ya lo dice el desplegable de arriba.
                  Fuera del gate `isTablet`: mirar la ficha lo puede hacer
                  cualquier rol que vea el cronómetro; operar el tramo no. */}
              <ActionButton
                variant="secondary"
                text={t('pages:surveillance.seeRoute')}
                onClick={handleNavigateToRoute}
              />

              {isTablet && canOperateTimer(currentUser, selectedRoute) && (
                <ButtonRow>
                  {/* Con un tramo pendiente `activeSegment` ya no es null, así
                      que Iniciar desaparece solo: el doble toque que encolaba
                      un segundo tramo —y que al sincronizar chocaba con el
                      guard de "ya hay un timer activo" y se descartaba en
                      silencio— deja de ser posible por construcción. */}
                  {!activeSegment && !timerActiveElsewhere && (
                    <ActionButton
                      text={t('modals:timerOverlay.startSegment')}
                      onClick={handleStartTimer}
                    />
                  )}
                  {activeSegment && (
                    <>
                      <ActionButton
                        text={t('modals:timerOverlay.changeDirection')}
                        variant="secondary"
                        onClick={handleChangeDirection}
                      />
                      <ActionButton
                        text={t('modals:timerOverlay.finishSegment')}
                        onClick={handleFinishSegment}
                      />
                    </>
                  )}
                </ButtonRow>
              )}
            </>
          )}
        </Popover>
      )}
    </ContainerOverlay>
  );
};

export default TimerOverlay;
